import React from 'react';
import { Grid, Stack, TextField } from '@mui/material';
import { Box, Typography } from 'components/index';
import { Close, Edit } from '@mui/icons-material';
import { renderCardField } from '../../components/general/Card';
import { CCReport, InputCCReportDTO } from './construction-cost-report.type';
import { format, formatCurrency, formatISODate } from 'common/utils/common.util';
import { renderDetails, renderTimePeriod } from './construction-cost-report.util';
import { useFormik } from 'formik';
import * as yup from 'yup';
import WhiteFab from '../common/WhiteFab';
import SimpleCard from '../general/SimpleCard';
import { ReportTypes } from 'common/utils/util.type';

interface ReportSummaryProps {
  report: CCReport;
  typeOfReport: ReportTypes;
  updateReport: (updatedReport: any) => void;
  filtersObject: any;
  minGSF: number;
  maxGSF: number;
  projectTypesString: string;
  minBudget: number;
  maxBudget: number;
  locations: any;
  minUnitCount: number;
  maxUnitCount: number;
  minStoryCount: number;
  maxStoryCount: number;
  minPeriodEnding: Date | undefined;
  maxPeriodEnding: Date | undefined;
  minBuildingCount: number;
  maxBuildingCount: number;
  minSiteArea: number;
  maxSiteArea: number;
  isEditMode: boolean;
  setIsEditMode: (isEditMode: boolean) => void;
  toggleEditMode: () => void;
}
type ObjectType = {
  [key: string]: any;
};

const CCReportSummary: React.FC<ReportSummaryProps> = ({
  report,
  typeOfReport,
  updateReport,
  minGSF,
  maxGSF,
  minBudget,
  maxBudget,
  locations,
  minUnitCount,
  maxUnitCount,
  minStoryCount,
  maxStoryCount,
  minPeriodEnding,
  maxPeriodEnding,
  minBuildingCount,
  maxBuildingCount,
  minSiteArea,
  maxSiteArea,
  isEditMode,
  setIsEditMode,
  toggleEditMode,
}) => {
  const noOfIncludedComps = report.comparableProjects?.filter(comp => comp.isIncluded)?.length || 0;

  const headerLine = 'The defined scope of work was to determine the average construction costs for the following Project Type:';

  const initialOrDefaultValues: Partial<InputCCReportDTO> = {
    name: report?.name ?? '',
    description: report?.description ?? '',
  };

  const formik = useFormik({
    initialValues: initialOrDefaultValues,
    enableReinitialize: true,
    validationSchema: yup.object({
      name: yup.string().trim().nonNullable().required('Required').min(1, 'Report name cannot be empty'),
      description: yup.string().trim().min(1, 'Description cannot be just whitespace'),
    }),
    onSubmit: values => {
      updateReport({ ...report, ...values });
      setIsEditMode(false);
    },
  });

  React.useEffect(() => {
    formik.setValues({ ...formik.values, ...initialOrDefaultValues });
  }, [report]);

  const canSubmit = React.useMemo(() => {
    if (!formik.isValid) return false;
    return formik.dirty;
  }, [formik]);

  const handleCancel = () => {
    formik.resetForm();
    setIsEditMode(false);
  };

  const comparablesSummary = {
    'Comparable Projects': (
      <Stack spacing={1} direction={'row'} alignItems={'center'}>
        <Typography variant='body2' fontWeight='regular' sx={{ whiteSpace: 'pre-wrap' }}>
          {`${noOfIncludedComps} comparable project${noOfIncludedComps === 1 ? '' : 's'}`}
        </Typography>
      </Stack>
    ),
    'Gross Building Areas range from':
      minGSF && maxGSF
        ? minGSF == maxGSF
          ? `${format(minGSF.toString(), 'number')}  Sq. Ft.`
          : `${format(minGSF.toString(), 'number')}  Sq. Ft.   to   ${format(maxGSF.toString(), 'number')}  Sq. Ft.`
        : '--',
    'Site Areas Range From':
      minSiteArea && maxSiteArea
        ? minSiteArea == maxSiteArea
          ? `${minSiteArea}  Acre${minSiteArea === 1 ? '' : 's'}`
          : `${minSiteArea}  Acre${minSiteArea === 1 ? '' : 's'}   to   ${maxSiteArea}  Acre${maxSiteArea === 1 ? '' : 's'}`
        : '--',
    'Unit Counts range from':
      minUnitCount && maxUnitCount
        ? minUnitCount == maxUnitCount
          ? `${minUnitCount}  Unit${minUnitCount === 1 ? '' : 's'}`
          : `${minUnitCount}  Unit${minUnitCount === 1 ? '' : 's'}   to   ${maxUnitCount}  Units`
        : '--',

    'Building Counts Range From':
      minBuildingCount && maxBuildingCount
        ? minBuildingCount == maxBuildingCount
          ? `${minBuildingCount}  Building${minBuildingCount === 1 ? '' : 's'}`
          : `${minBuildingCount}  Building${minBuildingCount === 1 ? '' : 's'}   to   ${maxBuildingCount}  Buildings`
        : '--',
    'Story Counts range from':
      minStoryCount && maxStoryCount
        ? minStoryCount == maxStoryCount
          ? `${minStoryCount}  ${minStoryCount === 1 ? 'Story' : 'Stories'}`
          : `${minStoryCount}  ${minStoryCount === 1 ? 'Story' : 'Stories'}   to   ${maxStoryCount}  Stories`
        : '--',
    'Budgets range from':
      minBudget && maxBudget
        ? minBudget == maxBudget
          ? `${formatCurrency(minBudget.toString())}`
          : `${formatCurrency(minBudget.toString())}   to   ${formatCurrency(maxBudget.toString())}`
        : '--',
    'Dates of Budgets range from':
      (!minPeriodEnding || isNaN(Date.parse(minPeriodEnding.toString()))) &&
      (!maxPeriodEnding || isNaN(Date.parse(maxPeriodEnding.toString())))
        ? '--'
        : `${formatISODate(minPeriodEnding?.toString() ?? '--', true)}   to   ${formatISODate(
            maxPeriodEnding?.toString() ?? '--',
            true,
          )} ${renderTimePeriod(minPeriodEnding, maxPeriodEnding)}`,

    Locations: renderDetails(locations),
    'Project Types': renderDetails(
      (report.comparableProjects?.filter(comp => comp.isIncluded).map(project => project.comp.projectType) ?? []) as string[],
    ),
  };

  return (
    <form onSubmit={formik.handleSubmit} style={{ height: '100%' }}>
      <SimpleCard
        heading={report.name}
        alignItems='unset'
        headerAction={
          typeOfReport == ReportTypes.CC ? (
            isEditMode ? (
              <WhiteFab aria-label='cancel' icon={<Close fontSize='small' />} textColor='error' size='small' onClick={handleCancel} />
            ) : (
              <WhiteFab aria-label='edit' icon={<Edit fontSize='small' />} textColor='primary' size='small' onClick={toggleEditMode} />
            )
          ) : undefined
        }
      >
        <Grid container spacing={6}>
          {typeOfReport == ReportTypes.CC && isEditMode && (
            <>
              {/* Report Name Section (Edit Mode Only) */}
              <Grid size={{ xs: 12 }}>
                <Box display='flex' flexDirection='column'>
                  <Typography variant='caption' color={'text'} fontWeight='medium' textTransform='capitalize'>
                    Report Name:
                  </Typography>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      name='name'
                      placeholder='Report Name'
                      variant='standard'
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={e => {
                        formik.handleBlur(e);
                        formik.submitForm(); // Save changes on blur
                      }}
                      error={formik.touched.name && Boolean(formik.errors.name)}
                      helperText={formik.touched.name && formik.errors.name}
                      fullWidth // Stretch to fit the grid item
                    />
                  </Grid>
                </Box>
              </Grid>
            </>
          )}
          {Object.entries(comparablesSummary).map(([field, value]) => renderCardField(field, value, 6))}
        </Grid>
      </SimpleCard>
    </form>
  );
};

export default CCReportSummary;
